import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import type { ShowErrorToastFn } from "../../../../mantine/notifications/useToast";
import { isApiError } from "../../../../utils/isApiError";
import { getFieldPaths } from "./getFieldPaths";

/**
 * Extract error message from various error value formats
 */
function getErrorMessage(errorValue: unknown, fallback: string): string {
  if (typeof errorValue === "string") {
    return errorValue;
  }

  if (Array.isArray(errorValue) && typeof errorValue[0] === "string") {
    return errorValue[0];
  }

  return fallback;
}

/**
 * Set field error if the path exists in fieldPaths, otherwise return the error message
 */
function handleFieldError<T extends FieldValues>(
  path: string,
  errorValue: unknown,
  fieldPaths: string[],
  setError: UseFormSetError<T>,
  fallbackMessage: string
): string | null {
  if (!path || typeof path !== "string") {
    return fallbackMessage;
  }

  const errorMessage = getErrorMessage(errorValue, fallbackMessage);

  if (fieldPaths.includes(path)) {
    setError(
      path as Path<T>,
      {
        type: "custom",
        message: errorMessage
      },
      { shouldFocus: true }
    );
    return null; // Error was handled
  }

  // For array field errors like "field.0.subfield", try to match parent "field"
  const pathSegments = path.split(".");
  for (let i = pathSegments.length - 1; i >= 0; i--) {
    const segment = pathSegments[i];
    if (/^\d+$/.test(segment)) {
      const parentPath = pathSegments.slice(0, i).join(".");

      if (!parentPath) {
        continue;
      }

      if (fieldPaths.includes(parentPath)) {
        setError(
          path as Path<T>,
          {
            type: "custom",
            message: errorMessage
          },
          { shouldFocus: true }
        );
        return null;
      }
    }
  }

  return errorMessage; // Return unmatched error
}

/**
 * Process error response recursively and collect unmatched errors
 */
function processErrors<T extends FieldValues>(
  errorData: unknown,
  fieldPaths: string[],
  setError: UseFormSetError<T>,
  fallbackMessage: string,
  pathPrefix = ""
): string[] {
  if (!errorData || typeof errorData !== "object") {
    return [];
  }

  if (Array.isArray(errorData)) {
    const unmatchedErrors: string[] = [];
    errorData.forEach((item, index) => {
      if (item && typeof item === "object") {
        const arrayItemPath = pathPrefix
          ? `${pathPrefix}.${index}`
          : `${index}`;
        const nestedErrors = processErrors(
          item,
          fieldPaths,
          setError,
          fallbackMessage,
          arrayItemPath
        );
        unmatchedErrors.push(...nestedErrors);
      }
    });
    return unmatchedErrors;
  }

  const unmatchedErrors: string[] = [];
  const errors = errorData as Record<string, unknown>;

  Object.entries(errors).forEach(([key, value]) => {
    const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;

    const isErrorMessageArray =
      Array.isArray(value) && value.length > 0 && typeof value[0] === "string";

    if (isErrorMessageArray) {
      const unmatchedError = handleFieldError(
        currentPath,
        value,
        fieldPaths,
        setError,
        fallbackMessage
      );
      if (unmatchedError) {
        unmatchedErrors.push(unmatchedError);
      }
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      // Recursively process nested objects
      const nestedErrors = processErrors(
        value,
        fieldPaths,
        setError,
        fallbackMessage,
        currentPath
      );
      unmatchedErrors.push(...nestedErrors);
    } else if (Array.isArray(value)) {
      const nestedErrors = processErrors(
        value,
        fieldPaths,
        setError,
        fallbackMessage,
        currentPath
      );
      unmatchedErrors.push(...nestedErrors);
    } else {
      // Handle leaf error values (strings)
      const unmatchedError = handleFieldError(
        currentPath,
        value,
        fieldPaths,
        setError,
        fallbackMessage
      );
      if (unmatchedError) {
        unmatchedErrors.push(unmatchedError);
      }
    }
  });

  return unmatchedErrors;
}

/**
 * Show unmatched errors as toast notifications
 */
function showUnmatchedErrors(
  showErrorToast: ShowErrorToastFn,
  errors: string[]
): void {
  errors.forEach((error) => {
    showErrorToast(error);
  });
}

/** Using `setError` from `useForm`, assigns incoming errors from an API response to their respective fields. */
export function setErrorsFromResponseData<T extends FieldValues>(
  showErrorToast: ShowErrorToastFn,
  error: unknown,
  fieldList: FieldValues,
  setError: UseFormSetError<T>,
  unknownErrorText: string,
  fieldNamePrefix?: string
) {
  if (
    !isApiError<Record<string, unknown>>(error) ||
    !error.response?.data ||
    error.response?.status !== 400
  ) {
    showErrorToast(unknownErrorText);
    return;
  }

  const fieldPaths = getFieldPaths(fieldList, fieldNamePrefix);
  const responseData = error.response.data;

  const unmatchedErrors = processErrors(
    responseData,
    fieldPaths,
    setError,
    unknownErrorText,
    fieldNamePrefix
  );

  showUnmatchedErrors(showErrorToast, unmatchedErrors);
}
